// @flow
import React, {useEffect} from 'react';
import type {Node} from 'react';
import moment from 'moment';
import type {FormType, UICalendarEventType} from '@wellstone-solutions/common';
import {Checkbox, Stack, Typography} from '@wellstone-solutions/web';
import {DaysOfMonth} from './DaysOfMonth';
import {DaysOfWeek} from './DaysOfWeek';
import {IntervalSelect} from './IntervalSelect';
import {FreqSelect} from './FreqSelect';
import {
  RECURRENCE_FREQ_MAP,
  frequencySelectItems,
} from 'modules/calendar/constants';

type PropsType = {
  form: FormType<UICalendarEventType>,
  alwaysRecurring?: boolean,
  isGroupEvent?: boolean,
  frequnecySelectItems?: Array<{label: string, value: string}>,
};

const DEFAULT_YEAR_DAY = 11;

export const Recurrence = ({
  form,
  alwaysRecurring = false,
  isGroupEvent = false,
  frequnecySelectItems = frequencySelectItems,
}: PropsType): Node => {
  const frequency = form.values.recurrence?.freq;
  const interval = form.values.recurrence?.interval;
  const isChecked = form.values.isRecurring;
  const label = isChecked ? 'Repeat every' : 'Repeat';

  // $FlowFixMe
  const isDisabled = form.initialValues?.isRecurring && !isGroupEvent;

  useEffect(() => {
    const {startDate, recurrence} = form.values;
    const {bymonthday, byweekday} = recurrence;

    if (
      frequency === RECURRENCE_FREQ_MAP.monthly &&
      bymonthday === 0 &&
      byweekday === ''
    ) {
      form.setFieldValue('recurrence.bymonthday', moment(startDate).date());
    }

    // initialize byweekday on weekly frequency if not set
    if (
      frequency === RECURRENCE_FREQ_MAP.weekly &&
      form.values.recurrence.byweekday === null
    ) {
      const dayAbbr = moment(form.values.startDate)
        .format('dddd')
        .slice(0, 2)
        .toUpperCase();

      form.setFieldValue('recurrence.byweekday', dayAbbr);
    }
  }, [
    form.values.startDate,
    frequency,
    form.values.recurrence.bymonthday,
    form.values.recurrence.bysetpos,
    form.values.recurrence.byweekday,
  ]);

  return (
    <Stack>
      <Stack direction={{xs: 'column', md: 'row'}}>
        {alwaysRecurring && (
          <Typography sx={{mr: 2, mt: 'auto', mb: 'auto'}}>{label}</Typography>
        )}
        {!alwaysRecurring && (
          <Checkbox
            disabled={isDisabled}
            checked={isChecked}
            label={label}
            value={isChecked}
            onChange={(event) => {
              form.setFieldValue('isRecurring', event.target.checked);
            }}
          />
        )}
        {isChecked && (
          <Stack direction="row" spacing={1}>
            <IntervalSelect
              onChange={(updatedInterval) => {
                form.setFieldValue('recurrence.interval', updatedInterval);
              }}
              value={interval}
            />

            <FreqSelect
              items={frequnecySelectItems}
              disabled={isDisabled}
              onChange={(freq) => {
                form.setFieldValue('recurrence.freq', freq);

                // When weekly, reset bymonthday just in case it was set
                if (
                  freq === RECURRENCE_FREQ_MAP.weekly &&
                  form.values.recurrence.bymonthday
                ) {
                  form.setFieldValue('recurrence.bymonthday', null);
                }

                // When yearly, we just set the year date to a default value
                if (freq === RECURRENCE_FREQ_MAP.yearly) {
                  form.setFieldValue('recurrence.byyearday', DEFAULT_YEAR_DAY);
                }
              }}
              value={frequency}
            />
          </Stack>
        )}
      </Stack>
      {isChecked && frequency === RECURRENCE_FREQ_MAP.weekly && (
        <Stack
          alignItems={{
            xs: 'start',
            md: 'center',
          }}
          direction={{
            xs: 'column',
            md: 'row',
          }}
          sx={{mt: 2}}
          spacing={2}>
          <Typography>Repeat on</Typography>
          <DaysOfWeek
            onChange={(byweekday) => {
              console.log('recurrence byweekday changed', byweekday);
              form.setFieldValue('recurrence.byweekday', byweekday);
            }}
            startDate={form.values.startDate}
            value={form.values.recurrence?.byweekday}
          />
        </Stack>
      )}
      {isChecked &&
        form.values.recurrence?.freq === RECURRENCE_FREQ_MAP.monthly && (
          <Stack
            alignItems={{
              xs: 'start',
              md: 'center',
            }}
            direction={{
              xs: 'column',
              md: 'row',
            }}
            sx={{mt: 2}}
            spacing={{
              xs: 0,
              md: 2,
            }}>
            <Typography>Repeat on</Typography>
            <DaysOfMonth
              form={form}
              recurrence={form.values.recurrence}
              startDate={form.values.startDate}
              onChange={(values) => {
                form.setFieldValue('recurrence', values);
              }}
            />
          </Stack>
        )}
      {isChecked && frequency === RECURRENCE_FREQ_MAP.yearly && (
        <Stack alignItems="center" direction="row" sx={{mt: 2}}>
          <Typography>{`On ${moment(form.values.startDate).format(
            'MMMM Do',
          )}`}</Typography>
        </Stack>
      )}
    </Stack>
  );
};
